import { Image, Picture, Box, Flex, Typography } from "@jewlr/storybook/core"
import React from "react"
import styled from "styled-components"

import { HomepageButton } from "areas/homepage/leefiori/components"
import { Link } from "components/common"
import { IS_UK, jewlr_t } from "helpers/application"
import { to_currency } from "helpers/number"

const VipCard = styled(Box).attrs({
  px: { _: 3, tablet: 8 },
  py: { _: 4, tablet: 5 },
})`
  background-color: ${({ theme }) => theme.colors.primaryColors.martini};
  width: 100%;
  margin: 0 auto;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.white};
  ${(props) => props.theme.mediaQueries.tablet`
    width: 65%;
  `}
  ${(props) => props.theme.mediaQueries.desktop`
    width: 36%;
  `}
`

const StepsTitle = styled(Typography).attrs({
  fontSize: 18,
  lineHeight: "1.5",
  mx: "auto",
})``

const TiersTitle = styled(Typography).attrs({
  as: "h2",
  fontSize: { _: 4, tablet: 5 },
  mb: 2,
})``

const VIPRewards = () => {
  const is_uk = IS_UK()

  const dollarSingular = is_uk ? "pound" : "dollar"
  const dollarUnit = is_uk ? "pounds" : "dollars"
  const freeShipping = is_uk
    ? `Free Express ${jewlr_t("terms.shipping")}`
    : `Free 2-day ${jewlr_t("terms.shipping")}`
  const stepFour = is_uk ? "step-four-uk" : "step-four"

  return (
    <Box contain pt={5} textAlign="center">
      <Box px={1}>
        <Image
          alt="VIP Rewards"
          mb={2}
          src={require("images/leefiori/info/vip-rewards/VIP-rewards.svg")}
          width={{ _: "50%", desktop: "16%", tablet: "30%" }}
        />
        <Typography
          as="h2"
          fontSize={{ _: 24, tablet: "32px" }}
          fontWeight="normal"
          mb={3}
        >
          (and what&apos;s in it for you!)
        </Typography>

        <Typography
          fontSize={{ _: 18, tablet: 20 }}
          lineHeight="copy"
          mb={{ _: 4, tablet: 5 }}
          mx="auto"
          width={{ _: "75%", desktop: "53%", tablet: "100%" }}
        >
          We know you want it all - so we&apos;re making it easier for you to
          get it! As a VIP Rewards member, you will earn Jewls with every
          purchase. Your Jewls are redeemable for {dollarUnit} towards future
          purchases. It&apos;s our way of thanking you.
        </Typography>
        <VipCard>
          <Typography as="h2" fontSize={20} px={2} variant="h2">
            Join today for FREE and receive 1,000 VIP Rewards bonus points!
          </Typography>
          <HomepageButton link="/signup" my={2} outline="white">
            SIGN UP
          </HomepageButton>
          <Typography fontSize={16} py={1}>
            Already have an account?{" "}
            <Link color="white" to="/signin" underline="true">
              Click here
            </Link>{" "}
            to sign in.
          </Typography>
        </VipCard>
      </Box>
      <Image
        alt="How It Works"
        mb={2}
        mt={8}
        src={require("images/leefiori/info/vip-rewards/how-it-works.svg")}
        width={{ _: "50%", desktop: "16%", tablet: "30%" }}
      />
      <Typography
        fontSize={{ _: 18, tablet: 20 }}
        lineHeight="copy"
        mx="auto"
        width={{ tablet: "70%" }}
      >
        It&apos;s easy to use the VIP Rewards program!
      </Typography>

      <Box
        alignItems="flex-start"
        display={{ _: "block", tablet: "flex" }}
        flexWrap="wrap"
        mx="auto"
        pb={4}
        width={{ _: "75%", desktop: "70%", tablet: "100%" }}
      >
        <Box flex="0 1 auto" width={{ _: "100%", tablet: "50%" }}>
          <Box>
            <Image
              alt="step one"
              mb={4}
              mt={8}
              src={require("images/leefiori/info/vip-rewards/step-one.svg")}
              width={{ _: "50%", tablet: "30%" }}
            />
          </Box>
          <StepsTitle width="90%">1. Activate your account</StepsTitle>
        </Box>
        <Box flex="0 1 auto" width={{ _: "100%", tablet: "50%" }}>
          <Box>
            <Image
              alt="step two"
              mb={4}
              mt={8}
              src={require("images/leefiori/info/vip-rewards/step-two.svg")}
              width={{ _: "50%", tablet: "30%" }}
            />
          </Box>
          <StepsTitle width={{ tablet: "70%" }}>
            2. Earn 10, 12 or 14 jewls per {dollarSingular} spent with every
            purchase
          </StepsTitle>
        </Box>
        <Box flex="0 1 auto" width={{ _: "100%", tablet: "50%" }}>
          <Box>
            <Image
              alt="step three"
              mb={4}
              mt={8}
              src={require("images/leefiori/info/vip-rewards/step-three.svg")}
              width={{ _: "50%", tablet: "30%" }}
            />
          </Box>
          <StepsTitle width={{ tablet: "75%" }}>
            3. Reach new levels to unlock exclusive perks, such as VIP-only
            sales
          </StepsTitle>
        </Box>
        <Box flex="0 1 auto" width={{ _: "100%", tablet: "50%" }}>
          <Box>
            <Image
              alt="step four"
              mb={4}
              mt={8}
              src={require(`images/leefiori/info/vip-rewards/${stepFour}.svg`)}
              width={{ _: "50%", tablet: "30%" }}
            />
          </Box>
          <StepsTitle width={{ tablet: "75%" }}>
            4. Redeem Jewls for {dollarUnit} toward future purchases at checkout
          </StepsTitle>
        </Box>
      </Box>

      <Box>
        <Image
          alt="VIP Rewards Tiers"
          mb={4}
          mt={8}
          src={require("images/leefiori/info/vip-rewards/VIP-rewards-tiers.svg")}
          width={{ _: "55%", desktop: "19%", tablet: "33%" }}
        />
      </Box>
      <Picture
        alt="VIP Rewards Tiers"
        fallback={require("images/leefiori/info/vip-rewards/desktop_2x.jpg")}
        lazyload={false}
        mb={8}
        srcSet={[
          {
            media: "(max-width: 767px)",
            sizes: "375vw",
            srcSet: `
                ${require("images/leefiori/info/vip-rewards/mobile.jpg")} 375w,
                ${require("images/leefiori/info/vip-rewards/mobile_2x.jpg")} 750w
              `,
          },
          {
            media: "(min-width: 768px)",
            sizes: "580",
            srcSet: `
                ${require("images/leefiori/info/vip-rewards/desktop.jpg")} 580w,
                ${require("images/leefiori/info/vip-rewards/desktop_2x.jpg")} 1160w
              `,
          },
        ]}
        width={{ _: "100%", tablet: "50%" }}
      />

      <Flex
        alignItems={{ _: "center", tablet: "baseline" }}
        flexDirection={{ _: "column", tablet: "row" }}
        justifyContent="space-evenly"
        margin="0px auto"
        pb={8}
        width="85%"
      >
        <Box mb={5}>
          <TiersTitle>Sapphire</TiersTitle>
          <Image
            alt="sapphire"
            mb={3}
            src={require("images/leefiori/info/vip-rewards/sapphire.svg")}
            width={{ _: "45%", tablet: "50%" }}
          />
          <Typography fontSize={16} lineHeight="1.6">
            0 to 4,999 Jewls
            <br />
            Earn 10 Jewls for every {to_currency(1)} spent
            <br />
            Receive 1,000 bonus Jewls for
            <br />
            activating your account
            <br />
            Early access to new {sl.config.site_title} products
          </Typography>
        </Box>

        <Box mb={5}>
          <TiersTitle>Ruby</TiersTitle>
          <Image
            alt="ruby"
            mb={3}
            src={require("images/leefiori/info/vip-rewards/ruby.svg")}
            width={{ _: "45%", tablet: "50%" }}
          />
          <Typography fontSize={16} lineHeight="1.6">
            5,000 to 8,499 Jewls
            <br />
            Earn 12 Jewls for every {to_currency(1)} spent
            <br />
            Early access to new {sl.config.site_title} products
            <br />
            <strong>{freeShipping}</strong>
          </Typography>
        </Box>
        <Box mb={5}>
          <TiersTitle>Diamond</TiersTitle>
          <Image
            alt="diamond"
            mb={3}
            src={require("images/leefiori/info/vip-rewards/diamond.svg")}
            width={{ _: "45%", tablet: "50%" }}
          />
          <Typography fontSize={16} lineHeight="1.6">
            8,500 + Jewls
            <br />
            Earn 14 Jewls for every {to_currency(1)} spent
            <br />
            Early access to new {sl.config.site_title} products
            <br />
            <strong>{freeShipping} </strong>
            <br />
            <strong>Virtual personal shopper</strong>
          </Typography>
        </Box>
      </Flex>
      <Box bg="primaryColors.whisper" mb={7} px={6} py={8}>
        <Typography fontSize={{ _: 20, tablet: 24 }} mb={2}>
          {" "}
          Redeem Jewls for {dollarUnit} toward your purchase at checkout
        </Typography>
        <Typography fontSize={{ _: "36px", tablet: "32px" }}>
          200 Jewls = {to_currency(1)}
        </Typography>
      </Box>
    </Box>
  )
}
export default VIPRewards
